import React, { useEffect } from 'react';
import {
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    Row,
    Col
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import type { CustomerStateType } from '../types/customer-states.types';
import ModalCreate from '../../../Components/Common/Modal';

export interface CustomerStateFormData {
    name: string;
    description?: string;
    type: CustomerStateType;
}

type CustomerStateFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (formData: CustomerStateFormData) => void | Promise<void>;
    initialData?: CustomerStateFormData | null;
};

export const CustomerStateFormModal: React.FC<CustomerStateFormModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    initialData,
}) => {
    const isEdit = !!initialData;

    const formik = useFormik<CustomerStateFormData>({
        enableReinitialize: true,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            type: initialData?.type || 'TRANSIENT',
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .max(100, 'Máximo 100 caracteres')
                .required('El nombre es requerido'),
            description: Yup.string()
                .max(500, 'Máximo 500 caracteres')
                .optional(),
            type: Yup.string()
                .oneOf(['INITIAL', 'FINAL', 'TRANSIENT'], 'Tipo inválido')
                .required('El tipo es requerido'),
        }),
        onSubmit: async (values) => {
            await onSubmit(values);
        },
    });

    // Reset form when modal is closed
    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Check if initial warning should be shown:
    const showInitialWarning = formik.values.type === 'INITIAL' && (!initialData || initialData.type !== 'INITIAL');

    return (
        <ModalCreate
            title={isEdit ? 'Editar Estado de Cliente' : 'Crear Estado de Cliente'}
            show={isOpen}
            handle={onClose}
            footer={
                <div className="hstack gap-2 justify-content-end w-100">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="btn-light"
                        disabled={formik.isSubmitting}
                    >
                        Cerrar
                    </Button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        id="add-btn"
                        form="customer-state-form"
                        disabled={!formik.isValid || formik.isSubmitting}
                    >
                        {isEdit ? 'Actualizar' : 'Agregar'}
                    </button>
                </div>
            }
        >
            <Form
                id="customer-state-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}
            >
                <Row className="g-3">
                    {/* Name */}
                    <Col xs={12}>
                        <FormGroup className="mb-0">
                            <Label for="form-state-name" className="form-label fw-semibold">
                                Nombre <span className="text-danger">*</span>
                            </Label>
                            <Input
                                name="name"
                                id="form-state-name"
                                type="text"
                                placeholder="Ingrese el nombre del estado (ej. Nuevo, Contactado)"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                invalid={!!(formik.touched.name && formik.errors.name)}
                            />
                            {formik.touched.name && formik.errors.name ? (
                                <FormFeedback type="invalid">
                                    {formik.errors.name}
                                </FormFeedback>
                            ) : null}
                        </FormGroup>
                    </Col>

                    {/* Description */}
                    <Col xs={12}>
                        <FormGroup className="mb-0">
                            <Label for="form-state-description" className="form-label fw-semibold">
                                Descripción
                            </Label>
                            <Input
                                name="description"
                                id="form-state-description"
                                type="textarea"
                                rows={3}
                                placeholder="Ingrese una breve descripción para este estado (opcional)"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                invalid={!!(formik.touched.description && formik.errors.description)}
                            />
                            {formik.touched.description && formik.errors.description ? (
                                <FormFeedback type="invalid">
                                    {formik.errors.description}
                                </FormFeedback>
                            ) : null}
                        </FormGroup>
                    </Col>

                    {/* Type */}
                    <Col xs={12}>
                        <FormGroup className="mb-0">
                            <Label for="form-state-type" className="form-label fw-semibold">
                                Tipo <span className="text-danger">*</span>
                            </Label>
                            <Input
                                name="type"
                                id="form-state-type"
                                type="select"
                                className="form-select"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.type}
                                invalid={!!(formik.touched.type && formik.errors.type)}
                            >
                                {isEdit && initialData?.type === 'INITIAL' && <option value="INITIAL">Inicial</option>}
                                {initialData?.type !== 'INITIAL' && <option value="TRANSIENT">Transitorio</option>}
                                {initialData?.type !== 'INITIAL' && <option value="FINAL">Final</option>}
                            </Input>
                            {formik.touched.type && formik.errors.type ? (
                                <FormFeedback type="invalid">
                                    {formik.errors.type}
                                </FormFeedback>
                            ) : null}

                            {/* Warning text */}
                            {showInitialWarning && (
                                <div className="alert alert-warning border-0 d-flex align-items-center mb-0 mt-3" role="alert">
                                    <i className="ri-error-warning-line me-2 fs-18 align-middle text-warning"></i>
                                    <div className="fs-12 text-warning-emphasis">
                                        <strong>Advertencia:</strong> Seleccionar el tipo <strong>Inicial</strong> reemplazará al estado inicial actual.
                                    </div>
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
            </Form>
        </ModalCreate>
    );
};

export default CustomerStateFormModal;
